import {
  action,
  computed,
  makeObservable,
  observable,
} from "mobx";

import {
  getFoodClassificationFromSuitabilityOption,
  getFoodSuitabilityOption,
  normalizeAllergenCodes,
} from "../../nutrition/foods/foodClassification";
import {
  buildNutritionFoodRecord,
  inflateNutritionFoodRecord,
} from "./nutritionRecords";
import {
  formatIntegerInput,
  formatMicronutrientInput,
  formatSingleDecimalInput,
  getInitialFoodQuantityInputValue,
  getResolvedFoodDraftValues,
  parseNumberInput,
} from "../../nutrition/foods/editor/foodDraft";
import {
  createEmptyMicronutrients,
  MICRONUTRIENT_KEYS as MICRO_FIELDS,
} from "../../nutrition/shared/micronutrients";
import {
  normalizeBuiltInCommonMeasureId,
} from "../../nutrition/shared/commonMeasures";
import OptimisticNutritionEntity, {
  buildComparablePayload,
  NutritionEntitySaveMode,
  clonePlainValue,
  getOptimisticEntityAnnotations,
} from "./OptimisticNutritionEntity";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function createEmptyMicros() {
  return createEmptyMicronutrients("");
}

function getDietaryClassification(draft) {
  return getFoodClassificationFromSuitabilityOption(draft?.dietarySuitability);
}

function getInitialMicrosValues(food) {
  const initialMicros = createEmptyMicros();
  const source =
    food?.micros && typeof food.micros === "object"
      ? food.micros
      : food?.micros_per_100g && typeof food.micros_per_100g === "object"
        ? food.micros_per_100g
        : {};

  MICRO_FIELDS.forEach((key) => {
    if (source[key] == null || source[key] === "") {
      return;
    }

    initialMicros[key] = formatMicronutrientInput(source[key]);
  });

  return initialMicros;
}

function getInitialCommonMeasures(food) {
  const sourceMeasures = Array.isArray(food?.commonMeasures)
    ? food.commonMeasures
    : Array.isArray(food?.common_measures)
      ? food.common_measures
      : [];
  const seenIds = new Set();

  return sourceMeasures.reduce((measures, measure) => {
    const measureKey = normalizeBuiltInCommonMeasureId(
      measure?.measureKey ?? measure?.id,
    );
    const grams = formatSingleDecimalInput(measure?.grams);

    if (!measureKey || !grams || seenIds.has(measureKey)) {
      return measures;
    }

    seenIds.add(measureKey);

    measures.push({
      id: String(measure?.id ?? measureKey),
      measureKey,
      grams,
    });

    return measures;
  }, []);
}

export function createInitialFoodDraft(food = null) {
  const initialCommonMeasures = getInitialCommonMeasures(food);
  const initialQuantityUnitKey =
    String(
      food?.quantityUnitKey ?? food?.selected_measure_id ?? food?.selectedMeasureId ?? "grams",
    ).trim() || "grams";
  const normalizedQuantityUnitKey =
    initialQuantityUnitKey === "grams"
      ? "grams"
      : normalizeBuiltInCommonMeasureId(initialQuantityUnitKey);
  const resolvedQuantityUnitKey =
    normalizedQuantityUnitKey !== "grams" &&
    initialCommonMeasures.some((measure) => measure.measureKey === normalizedQuantityUnitKey)
      ? normalizedQuantityUnitKey
      : "grams";

  return {
    name: food?.name ?? "",
    type: food?.type === "supplement" ? "supplement" : "food",
    quantity: getInitialFoodQuantityInputValue({
      quantity: food?.quantity,
      quantityReferenceG: food?.quantityReferenceG,
      reference_quantity_g: food?.reference_quantity_g,
      quantityUnitKey: resolvedQuantityUnitKey,
      selected_measure_id: food?.selected_measure_id,
      selectedMeasureId: food?.selectedMeasureId,
      commonMeasures: initialCommonMeasures,
    }),
    quantityUnitKey: resolvedQuantityUnitKey,
    selected_measure_id:
      resolvedQuantityUnitKey === "grams" ? null : resolvedQuantityUnitKey,
    energy: food?.calories != null ? formatIntegerInput(food.calories) : "",
    protein: food?.protein != null ? formatSingleDecimalInput(food.protein) : "",
    carbs: food?.carbs != null ? formatSingleDecimalInput(food.carbs) : "",
    fats: food?.fats != null ? formatSingleDecimalInput(food.fats) : "",
    fiber: food?.fiber != null ? formatSingleDecimalInput(food.fiber) : "",
    website: typeof food?.website === "string" ? food.website : "",
    allergens: normalizeAllergenCodes(food?.allergens ?? food?.allergen_codes),
    allergenDataComplete:
      food?.allergenDataComplete ?? food?.allergen_data_complete ?? false,
    dietarySuitability: getFoodSuitabilityOption(food),
    microsExpanded: false,
    micros: getInitialMicrosValues(food),
    commonMeasures: initialCommonMeasures,
  };
}

function inflateRuntimeFoodRecord(record, options = {}) {
  return inflateNutritionFoodRecord(
    {
      ...record,
      common_measures: record?.common_measures ?? record?.commonMeasures,
    },
    { coach: options.coach ?? record?.coach ?? null },
  );
}

function buildFoodMicrosForSave(draft = {}) {
  return MICRO_FIELDS.reduce((accumulator, key) => {
    accumulator[key] = parseNumberInput(draft?.micros?.[key]) ?? 0;
    return accumulator;
  }, {});
}

function buildFoodRecordFromDraft(draft, metadata = {}) {
  const resolvedDraft = getResolvedFoodDraftValues(draft);
  const dietaryClassification = getFoodClassificationFromSuitabilityOption(
    draft?.dietarySuitability,
  );

  return buildNutritionFoodRecord(
    {
      id: metadata.id,
      name: String(draft?.name ?? "").trim(),
      quantityUnitKey: draft?.quantityUnitKey,
      selected_measure_id: draft?.selected_measure_id,
      quantityReferenceG: resolvedDraft.resolvedQuantityInGrams ?? 100,
      calories: resolvedDraft.energyValue ?? 0,
      carbs: resolvedDraft.carbsValue ?? 0,
      protein: resolvedDraft.proteinValue ?? 0,
      fats: resolvedDraft.fatsValue ?? 0,
      fiber: resolvedDraft.fiberValue ?? 0,
      micros: buildFoodMicrosForSave(draft),
      commonMeasures: Array.isArray(draft?.commonMeasures)
        ? draft.commonMeasures
        : [],
      website: resolvedDraft.normalizedWebsite,
      type: draft?.type,
      allergens: Array.isArray(draft?.allergens) ? draft.allergens : [],
      allergenDataComplete: draft?.allergenDataComplete === true,
      isVegetarian: dietaryClassification.is_vegetarian,
      isVegan: dietaryClassification.is_vegan,
      dietaryDataComplete: dietaryClassification.dietary_data_complete,
      origin: metadata.origin ?? null,
      brand: metadata.brand ?? null,
      store: metadata.store ?? null,
    },
    {
      createdAt: metadata.createdAt,
      updatedAt: metadata.updatedAt,
      rev: metadata.rev,
    },
  );
}

export class NutritionFood extends OptimisticNutritionEntity {
  entityType = "food";
  id = "";
  origin = null;
  brand = null;
  store = null;
  createdAt = null;
  updatedAt = null;
  coach = null;
  draft = createInitialFoodDraft();

  constructor(data = {}, options = {}) {
    super({
      store: options.store,
      saveMode: NutritionEntitySaveMode.MANUAL,
    });

    const runtimeSource =
      Array.isArray(data?.nutrients_per_100g) || Array.isArray(data?.common_measures)
        ? inflateRuntimeFoodRecord(data, options)
        : data;
    const runtimeFood = createInitialFoodDraft(runtimeSource);
    this.id = String(data?.id ?? "").trim().toLowerCase();
    this.origin = runtimeSource?.origin ?? data?.origin ?? null;
    this.brand = runtimeSource?.brand ?? data?.brand ?? null;
    this.store = runtimeSource?.store ?? data?.store ?? null;
    this.createdAt = data?.createdAt ?? null;
    this.updatedAt = data?.updatedAt ?? null;
    this.coach = options.coach ?? data?.coach ?? null;
    this.draft = runtimeFood;
    this.initializeFromServerSnapshot(runtimeFood, { rev: data?._rev });

    makeObservable(this, {
      ...getOptimisticEntityAnnotations(),
      entityType: observable,
      id: observable,
      origin: observable.ref,
      brand: observable.ref,
      store: observable.ref,
      createdAt: observable.ref,
      updatedAt: observable.ref,
      draft: observable.ref,
      name: computed,
      type: computed,
      quantity: computed,
      quantityUnitKey: computed,
      selected_measure_id: computed,
      quantityReferenceG: computed,
      reference_quantity_g: computed,
      calories: computed,
      carbs: computed,
      protein: computed,
      fats: computed,
      fiber: computed,
      allergens: computed,
      allergenDataComplete: computed,
      isVegetarian: computed,
      isVegan: computed,
      dietaryDataComplete: computed,
      micros: computed,
      nutrients: computed,
      commonMeasures: computed,
      displayMeasures: computed,
      website: computed,
      hasOrigin: computed,
      isCoachOwned: computed,
      isCatalogLinked: computed,
      classificationSummary: computed,
      isClassificationComplete: computed,
      searchableText: computed,
      createdByMe: computed,
      replaceDraftSnapshot: action.bound,
      applyServerMetadata: action.bound,
      applyServerRecord: action.bound,
      tryApplyNoRefreshServerAck: action.bound,
      applyDraft: action.bound,
    });
  }

  static buildDraftFromRecord(record, options = {}) {
    return createInitialFoodDraft(inflateNutritionFoodRecord(record, options));
  }

  static buildRecordFromDraft(draft, metadata = {}) {
    return buildFoodRecordFromDraft(draft, metadata);
  }

  static fromRecord(record, options = {}) {
    return new NutritionFood(inflateNutritionFoodRecord(record, options), options);
  }

  applyServerRecord(record, options = {}) {
    this.applyServerMetadata(record, options);
    this.mergeServerSnapshot(this.buildDraftFromServerRecord(record, options), {
      rev: record?._rev,
    });
  }

  applyServerMetadata(record, options = {}) {
    const runtimeFood = inflateRuntimeFoodRecord(record, options);
    this.origin = runtimeFood.origin ?? null;
    this.brand = runtimeFood.brand ?? null;
    this.store = runtimeFood.store ?? null;
    this.createdAt = record?.createdAt ?? this.createdAt ?? null;
    this.updatedAt = record?.updatedAt ?? this.updatedAt ?? null;
    return runtimeFood;
  }

  buildDraftFromServerRecord(record, options = {}) {
    return createInitialFoodDraft(inflateRuntimeFoodRecord(record, options));
  }

  buildComparablePersistedPayloadFromDraft(draft = this.draft, options = {}) {
    return buildComparablePayload(buildFoodRecordFromDraft(draft, {
      id: options.id ?? this.id,
      origin: options.origin ?? this.origin,
      brand: options.brand ?? this.brand,
      store: options.store ?? this.store,
      createdAt: options.createdAt ?? this.createdAt,
      updatedAt: options.updatedAt ?? this.updatedAt,
      rev: options.rev ?? this._rev,
    }));
  }

  buildComparablePersistedPayloadFromServer(record, options = {}) {
    const runtimeFood = inflateRuntimeFoodRecord(record, options);
    return this.buildComparablePersistedPayloadFromDraft(
      createInitialFoodDraft(runtimeFood),
      {
        id: record?.id ?? this.id,
        origin: runtimeFood.origin ?? record?.origin ?? null,
        brand: runtimeFood.brand ?? record?.brand ?? null,
        store: runtimeFood.store ?? record?.store ?? null,
        createdAt: record?.createdAt,
        updatedAt: record?.updatedAt,
        rev: record?._rev,
      },
    );
  }

  tryApplyNoRefreshServerAck(record, options = {}) {
    const comparablePayload = this.buildComparablePersistedPayloadFromServer(record, options);
    if (comparablePayload == null) {
      return false;
    }

    this.applyServerMetadata(record, options);
    return this.tryProcessComparableServerAck({
      nextSnapshot: this.buildDraftFromServerRecord(record, options),
      nextRev: record?._rev,
      comparablePayload,
      finalize: options.finalizePendingAck !== false,
    });
  }

  get isPersistable() {
    return getResolvedFoodDraftValues(this.draft).canSave;
  }

  getDraftSnapshot() {
    return clonePlainValue(this.draft);
  }

  replaceDraftSnapshot(snapshot) {
    this.draft = clonePlainValue(snapshot ?? createInitialFoodDraft());
  }

  get name() {
    return String(this.draft?.name ?? "");
  }

  get type() {
    return this.draft?.type === "supplement" ? "supplement" : "food";
  }

  get quantity() {
    return parseNumberInput(this.draft?.quantity) ?? 100;
  }

  get quantityUnitKey() {
    const quantityUnitKey = String(this.draft?.quantityUnitKey ?? "grams").trim();
    return quantityUnitKey || "grams";
  }

  get selected_measure_id() {
    return this.quantityUnitKey === "grams" ? null : this.quantityUnitKey;
  }

  get quantityReferenceG() {
    return getResolvedFoodDraftValues(this.draft).resolvedQuantityInGrams ?? 100;
  }

  get reference_quantity_g() {
    return this.quantityReferenceG;
  }

  get calories() {
    return parseNumberInput(this.draft?.energy) ?? 0;
  }

  get carbs() {
    return parseNumberInput(this.draft?.carbs) ?? 0;
  }

  get protein() {
    return parseNumberInput(this.draft?.protein) ?? 0;
  }

  get fats() {
    return parseNumberInput(this.draft?.fats) ?? 0;
  }

  get fiber() {
    return parseNumberInput(this.draft?.fiber) ?? 0;
  }

  get allergens() {
    return Array.isArray(this.draft?.allergens) ? this.draft.allergens : [];
  }

  get allergenDataComplete() {
    return this.draft?.allergenDataComplete === true;
  }

  get isVegetarian() {
    return getDietaryClassification(this.draft).is_vegetarian;
  }

  get isVegan() {
    return getDietaryClassification(this.draft).is_vegan;
  }

  get dietaryDataComplete() {
    return getDietaryClassification(this.draft).dietary_data_complete === true;
  }

  get micros() {
    return buildFoodMicrosForSave(this.draft);
  }

  get nutrients() {
    return {
      calories: this.calories,
      carbs: this.carbs,
      protein: this.protein,
      fats: this.fats,
      fiber: this.fiber,
    };
  }

  get commonMeasures() {
    return Array.isArray(this.draft?.commonMeasures) ? this.draft.commonMeasures : [];
  }

  get displayMeasures() {
    return this.commonMeasures.map((measure) => ({
      ...measure,
      grams: parseNumberInput(measure?.grams) ?? 0,
    }));
  }

  get website() {
    return String(this.draft?.website ?? "");
  }

  get hasOrigin() {
    return Boolean(this.origin);
  }

  get isCoachOwned() {
    return !this.isCatalogLinked;
  }

  get isCatalogLinked() {
    return Boolean(this.origin?.catalog_food_id || this.origin?.catalog_recipe_id);
  }

  get classificationSummary() {
    return {
      allergens: this.allergens,
      isVegetarian: this.isVegetarian,
      isVegan: this.isVegan,
    };
  }

  get isClassificationComplete() {
    return this.allergenDataComplete && this.dietaryDataComplete;
  }

  get searchableText() {
    return [
      this.name,
      this.brand,
      this.store,
      this.website,
      this.type,
    ]
      .filter(hasText)
      .join(" ")
      .toLowerCase();
  }

  get createdByMe() {
    return true;
  }

  applyDraft(nextDraft, options = {}) {
    this.applyDraftSnapshot(nextDraft, options);
  }

  buildPersistableRecord(options = {}) {
    return buildFoodRecordFromDraft(this.draft, {
      id: this.id,
      origin: this.origin,
      brand: this.brand,
      store: this.store,
      createdAt: options.createdAt ?? this.createdAt,
      updatedAt: options.updatedAt ?? this.updatedAt,
      rev: options.rev ?? this._rev,
    });
  }

  toFirestore(options = {}) {
    return buildFoodRecordFromDraft(this.draft, {
      id: this.id,
      origin: this.origin,
      brand: this.brand,
      store: this.store,
      createdAt: options.createdAt,
      updatedAt: options.updatedAt,
      rev: options.rev ?? this._rev,
    });
  }

  toSnapshot({ quantityG, selectedMeasureId = null, id = this.id } = {}) {
    const record = this.toFirestore();
    const resolvedSelectedMeasureId =
      selectedMeasureId ?? this.selected_measure_id ?? null;
    const snapshot = {
      id: String(id ?? this.id ?? "").trim().toLowerCase(),
      type: "food",
      name: this.name,
      quantity_g: quantityG ?? this.quantityReferenceG,
      nutrients_per_100g: record.nutrients_per_100g,
      common_measures: record.common_measures,
      allergen_codes: record.allergen_codes,
      allergen_data_complete: record.allergen_data_complete,
      is_vegetarian: record.is_vegetarian,
      is_vegan: record.is_vegan,
      dietary_data_complete: record.dietary_data_complete,
    };

    if (record.origin) {
      snapshot.origin = record.origin;
    }

    if (resolvedSelectedMeasureId) {
      snapshot.selected_measure_id = resolvedSelectedMeasureId;
    }

    return snapshot;
  }
}

export default NutritionFood;
